/**
 * Timesheet Settings API Client
 * Handles API calls for organisation timesheet settings
 */

import { apiClient, ApiError } from '@/services/apiClient';
import type { OrganisationTimesheetSettingsDto } from '@/types/api';
import { 
  fromOrganisationTimesheetSettingsDto,
  toOrganisationTimesheetSettingsDto 
} from './mappers';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';

/**
 * Get organisation timesheet settings from API
 * @returns Organisation settings with RowVersion for concurrency control
 */
export async function getOrganisationSettings(): Promise<OrganisationTimesheetSettings & { rowVersion: string }> {
  const dto = await apiClient.get<OrganisationTimesheetSettingsDto>('/settings/timesheet');
  return fromOrganisationTimesheetSettingsDto(dto);
}

/**
 * Update organisation timesheet settings via API
 * @param settings - The settings to update
 * @param rowVersion - Current RowVersion for optimistic concurrency control
 * @returns Updated settings with new RowVersion
 * @throws ApiError with status 409 if concurrency conflict occurs
 */
export async function updateOrganisationSettings(
  settings: OrganisationTimesheetSettings,
  rowVersion: string
): Promise<OrganisationTimesheetSettings & { rowVersion: string }> {
  const dto = toOrganisationTimesheetSettingsDto(settings, rowVersion);
  
  try {
    const updatedDto = await apiClient.put<OrganisationTimesheetSettingsDto>('/settings/timesheet', dto);
    return fromOrganisationTimesheetSettingsDto(updatedDto);
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      throw new ApiError(
        409,
        'Conflict',
        'Settings have been modified by another user. Please refresh and try again.'
      );
    }
    throw error;
  }
}

/**
 * Fetch the list of available public holiday countries from the backend
 */
export async function getPublicHolidayCountries(): Promise<Array<{ countryCode: string; name: string }>> {
  // Static reference list (backend caches it for 24h); safe to cache client-side too.
  return apiClient.get<Array<{ countryCode: string; name: string }>>(
    '/settings/public-holiday-countries',
    { cacheTtlMs: 5 * 60_000 }
  );
}

export interface PublicHolidaySyncResult {
  syncedCount: number;
  deletedCount: number;
  absencesCreated: number;
  absencesDeleted: number;
  startYear: number;
  endYear: number;
}

/**
 * Trigger a sync of public holidays from the Nager.Date API for the configured country.
 * Fetches previous year, current year, and next year.
 */
export async function syncPublicHolidays(): Promise<PublicHolidaySyncResult> {
  return apiClient.post<PublicHolidaySyncResult>('/settings/public-holidays/sync', {});
}
